import React, { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { Button } from '../../components/common/Button';
import { Card, CardContent } from '../../components/common/Card';
import { permissionSlipApiService } from '../../services/permissionSlipApiService';
import { useAppSelector } from '../../store/hooks';

import { DistributionDashboard } from './components/DistributionDashboard';
import { FormsList } from './components/FormsList';

export const PermissionSlipsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [stats, setStats] = useState({
    activeForms: 0,
    recentSubmissions: 0,
    totalSubmissions: 0,
    completionRate: 0,
  });

  // Check user permissions
  const canCreateForms = user?.role === 'district_admin' || 
                        user?.role === 'school_admin' || 
                        user?.role === 'teacher';

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const summary =
          await permissionSlipApiService.getDistributionDashboardSummary();
        setStats({
          activeForms: summary.active_forms,
          recentSubmissions: summary.recent_submissions,
          totalSubmissions: summary.total_submissions,
          completionRate: summary.overall_completion_rate,
        });
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load permission slip summary'
        );
      } finally {
        setLoading(false);
      }
    };

    void loadStats();
  }, []);

  const handleCreateNew = () => {
    navigate('/permission-slips/create');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading permission slips...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Permission Slips
            </h1>
            <p className="text-gray-600">
              Create, distribute, and manage digital permission slips for your school
            </p>
          </div>
          {canCreateForms && (
            <Button
              variant="primary"
              onClick={handleCreateNew}
              className="flex items-center gap-2"
            >
              <span>📝</span>
              Create New Form
            </Button>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent>
            <p className="text-sm text-gray-600 mb-2">Active Forms</p>
            <p className="text-3xl font-bold text-gray-900">
              {stats.activeForms}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-sm text-gray-600 mb-2">Recent Submissions</p>
            <p className="text-3xl font-bold text-green-600">
              {stats.recentSubmissions}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-sm text-gray-600 mb-2">Total Submissions</p>
            <p className="text-3xl font-bold text-yellow-600">
              {stats.totalSubmissions}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-sm text-gray-600 mb-2">Completion Rate</p>
            <p className="text-3xl font-bold text-blue-600">
              {stats.completionRate}%
            </p>
          </CardContent>
        </Card>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-red-400">⚠️</span>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Permission Slip Forms
          </h2>
          <FormsList />
        </div>
      </div>

      {canCreateForms && (
        <div className="bg-white rounded-lg shadow mt-8 p-6">
          <DistributionDashboard />
        </div>
      )}
    </div>
  );
};